/**
 * Análises › Relatórios salvos (D523; doc 20 §4.3): a saved report is a NAME and a QUERY — the request the explorer sends, never an answer
 * (the BI answers are `no-store`; opening a report asks the server again, inside the operator's own access).
 *
 * The reports live on the SERVER, per operator (`/v1/analytics/reports`, table `vello.bi_saved_reports`): the same list on any browser, the
 * query validated by the planner, an uncheckable personal value refused, and every write audited. This file is the pure half on the
 * client: how the explorer's state becomes the query the server stores, how a stored query becomes a state again, and the ONE-TIME
 * import of what D522 kept in the browser's `localStorage` (read once, offered to the server, then cleared).
 */
import { type CompareAgainst, type CompareKind, type QueryRequest } from '@vello/analytics-semantic';
import { type AnalyticsBiSavedReport, type ApiError } from '@vello/api-client';
import { BI_ALL_PRESETS, type BiPeriod, type BiPreset, lastDaysValid } from './bi-core.js';
import { stateFromParts } from './bi-filters.js';
import { GRAINS, PAGE_SIZES, RANKING_SIZES, type ExplorerState, defaultExplorerState, explorerRequest, parseExplorerState } from './bi-explorer.js';

export const MAX_REPORTS = 50;
export const MAX_NAME = 80;

export function cleanName(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim().slice(0, MAX_NAME);
}

/** A saved report as the screens use it: the server's report and, when the explorer can show its query, the explorer state it opens as. */
export interface SavedReport {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly query: QueryRequest;
  /** It cuts by a person or an order: opening it needs `analytics:view_personal` (the server re-checks at every run). */
  readonly personal: boolean;
  /** Undefined when the explorer cannot show the query (a period it has no control for): the report is listed but cannot be opened there. */
  readonly state: ExplorerState | undefined;
}

export function savedReportView(r: AnalyticsBiSavedReport): SavedReport {
  return { id: r.id, name: r.name, createdAt: r.createdAt, updatedAt: r.updatedAt, query: r.query, personal: r.personal, state: stateFromQuery(r.query) };
}

// ───────────────────────────── state ⇄ query ─────────────────────────────

/**
 * The query the server stores for a state. No operator scope in it (the switcher's market and country apply when the report RUNS), no
 * page (a report is the question), and the automatic comparison written as the comparison the last answer actually used — a saved report
 * is the same question every time it opens.
 */
export function reportQuery(state: ExplorerState, options: { readonly fallback?: boolean } = {}): QueryRequest {
  return explorerRequest({ ...state, page: 0 }, [], { page: false, ...(options.fallback === true ? { fallback: true } : {}) });
}

const PRESETS: readonly string[] = BI_ALL_PRESETS;
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * The explorer state a stored query opens as, or undefined when the explorer has no control for it (a relative period outside its six
 * presets). A query saved from a ranking comes back as a ranking when it has the ranking's shape (one measure, one cut, sorted by it, a
 * ranking size); anything else is a table.
 */
export function stateFromQuery(q: QueryRequest): ExplorerState | undefined {
  if (!isRecord(q) || !Array.isArray(q.metrics) || !isRecord(q.period)) return undefined;
  let period: BiPeriod;
  if ('relative' in q.period) {
    if (q.period.relative === 'last_days') {
      if (typeof q.period.days !== 'number' || !lastDaysValid(q.period.days)) return undefined;
      period = { kind: 'lastDays', days: q.period.days };
    } else {
      if (!PRESETS.includes(q.period.relative)) return undefined;
      period = { kind: 'preset', preset: q.period.relative as BiPreset };
    }
  } else {
    period = { kind: 'custom', from: q.period.from, to: q.period.to };
  }
  const base = defaultExplorerState();
  const grain = q.granularity ?? 'total';
  if (!GRAINS.includes(grain)) return undefined;
  const sort = q.sort?.[0];
  const dimensions = [...(q.dimensions ?? [])];
  const limit = q.limit ?? base.pageSize;
  const ranking = q.metrics.length === 1 && dimensions.length === 1 && grain === 'total' && sort?.by === q.metrics[0] && (RANKING_SIZES as readonly number[]).includes(limit);
  const pageSize = ranking ? limit : (PAGE_SIZES as readonly number[]).includes(limit) ? limit : base.pageSize;
  const compare = q.compare === undefined ? ('none' as const) : q.compare;
  const against: CompareAgainst = q.compareAgainst ?? base.upToNowAgainst;
  const { filters, anyOf, having } = stateFromParts({ ...(q.filters !== undefined ? { filters: q.filters } : {}), ...(q.anyOf !== undefined ? { anyOf: q.anyOf } : {}), ...(q.having !== undefined ? { having: q.having } : {}) });
  return {
    ...base,
    mode: ranking ? 'ranking' : 'table',
    metrics: [...q.metrics],
    dimensions,
    grain,
    filters,
    anyOf,
    having,
    period,
    compare: compare as CompareKind | 'none',
    upToNowAgainst: against,
    ...(sort !== undefined ? { sortBy: sort.by } : {}),
    sortDir: sort !== undefined ? (sort.direction ?? (q.metrics.includes(sort.by) ? 'desc' : 'asc')) : base.sortDir,
    pageSize,
    page: 0,
  };
}

// ───────────────────────────── D522's browser reports: read once, offered to the server, cleared ─────────────────────────────

/** The slice of `Storage` the one-time import needs (a test passes a plain object). */
export interface ReportStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

const LEGACY_VERSION = 1;

/** A report as D522 kept it in the browser. */
export interface LegacyReport {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly state: ExplorerState;
}

export function legacyReportsKey(ownerId: string): string {
  return `vello.admin.bi.reports.v${LEGACY_VERSION}.${ownerId === '' ? 'anonymous' : ownerId}`;
}

/** Read a stored list; anything unreadable is dropped one report at a time (a corrupt entry never hides the others). */
export function parseLegacyReports(raw: string | null): readonly LegacyReport[] {
  if (raw === null) return [];
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!isRecord(data) || data.version !== LEGACY_VERSION || !Array.isArray(data.reports)) return [];
  const out: LegacyReport[] = [];
  for (const item of (data.reports as unknown[]).slice(0, MAX_REPORTS)) {
    if (!isRecord(item)) continue;
    const state = parseExplorerState(item.state);
    if (typeof item.id !== 'string' || item.id === '' || typeof item.name !== 'string' || cleanName(item.name) === '' || typeof item.createdAt !== 'string' || typeof item.updatedAt !== 'string' || state === undefined) continue;
    out.push({ id: item.id, name: cleanName(item.name), createdAt: item.createdAt, updatedAt: item.updatedAt, state });
  }
  return out;
}

/** What is waiting in this browser for this operator (never throws: blocked storage is simply nothing). */
export function readLegacyReports(storage: ReportStorage | undefined, ownerId: string): readonly LegacyReport[] {
  if (storage === undefined) return [];
  try {
    return parseLegacyReports(storage.getItem(legacyReportsKey(ownerId)));
  } catch {
    return [];
  }
}

/** Forget the browser's copy. Where the store cannot remove an item, an empty list is written over it. */
export function clearLegacyReports(storage: ReportStorage | undefined, ownerId: string): boolean {
  if (storage === undefined) return false;
  try {
    if (storage.removeItem !== undefined) storage.removeItem(legacyReportsKey(ownerId));
    else storage.setItem(legacyReportsKey(ownerId), JSON.stringify({ version: LEGACY_VERSION, reports: [] }));
    return true;
  } catch {
    return false;
  }
}

export interface ImportOutcome {
  /** How many reports the browser held. */
  readonly found: number;
  readonly imported: number;
  /** A report of that name was already on the server (an earlier import, or the same name made there). */
  readonly alreadyThere: number;
  /** The server will never accept it (a period or cut it no longer allows, a name it refuses, a person the operator may not keep). */
  readonly refused: number;
  /** The browser's copy was cleared. False when something was left for a later try (a transient failure, the limit, or storage that would not clear). */
  readonly cleared: boolean;
}

type SaveResult = { readonly ok: true } | { readonly ok: false; readonly error: Pick<ApiError, 'code' | 'status' | 'reasonCode'> };

/**
 * Offer the browser's reports to the server, oldest first, then clear the browser's copy. It clears ONLY when nothing was left to try
 * again: a report the server refused for ITS CONTENT (it never will accept it) is counted and dropped; a refusal that depends on the
 * moment (the network, a busy or unavailable server, the 50-report limit, or an operator who may not keep reports at all) leaves the whole copy where it is.
 */
export async function importLegacyReports(input: {
  readonly storage: ReportStorage | undefined;
  readonly ownerId: string;
  /** The names already on the server. */
  readonly existing: readonly string[];
  readonly save: (name: string, query: QueryRequest) => Promise<SaveResult>;
}): Promise<ImportOutcome> {
  const legacy = readLegacyReports(input.storage, input.ownerId);
  if (legacy.length === 0) return { found: 0, imported: 0, alreadyThere: 0, refused: 0, cleared: false };
  const taken = new Set(input.existing.map((n) => n.toLowerCase()));
  let imported = 0;
  let alreadyThere = 0;
  let refused = 0;
  let later = false;
  for (const report of [...legacy].sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0))) {
    if (taken.has(report.name.toLowerCase())) {
      alreadyThere += 1;
      continue;
    }
    let result: SaveResult;
    try {
      result = await input.save(report.name, reportQuery(report.state));
    } catch {
      later = true;
      break;
    }
    if (result.ok) {
      imported += 1;
      taken.add(report.name.toLowerCase());
      continue;
    }
    const { status, reasonCode } = result.error;
    if (reasonCode === 'REPORT_NAME_TAKEN') alreadyThere += 1;
    else if (reasonCode === 'REPORT_LIMIT_REACHED') {
      later = true;
      break;
    } else if (status === 400 || reasonCode === 'ANALYTICS_PERSONAL_FORBIDDEN') refused += 1;
    else {
      later = true;
      break;
    }
  }
  const cleared = !later && clearLegacyReports(input.storage, input.ownerId);
  return { found: legacy.length, imported, alreadyThere, refused, cleared };
}
